import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { type GitHubDiscussionsPayload, parseGitHubDiscussionsPayload } from "./githubDiscussionsSchema";
import { safeGitHubUrl } from "./githubRenderUtils";

interface GitHubDiscussionsRendererProps {
  block: StreamingBlock;
}

export const GitHubDiscussionsRenderer = (props: GitHubDiscussionsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubDiscussionsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubDiscussionsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_discussions_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().discussions.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbBrandGithub class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {rt.t("github_discussions_card.title", { repo: data().repo })}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().discussions.length === 1
                    ? rt.t("github_discussions_card.results_one")
                    : rt.t("github_discussions_card.results", { count: String(data().discussions.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().discussions}>
                  {(discussion) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <span class="shrink-0 font-mono text-xs text-muted-text">{discussion.reference}</span>
                          <Show
                            when={safeGitHubUrl(discussion.url)}
                            fallback={<span class="truncate text-sm text-primary-text">{discussion.title}</span>}
                          >
                            {(href) => (
                              <a
                                href={href()}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="truncate text-sm text-brand hover:underline"
                              >
                                {discussion.title}
                              </a>
                            )}
                          </Show>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(discussion.updatedAt)}</span>
                      </div>
                      <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
                        <Show when={discussion.answered}>
                          <span class="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {rt.t("github_discussions_card.answered")}
                          </span>
                        </Show>
                        <Show when={discussion.category}>
                          {(category) => <span class="truncate">{category()}</span>}
                        </Show>
                        <Show when={discussion.author}>{(author) => <span class="truncate">{author()}</span>}</Show>
                        <Show when={discussion.commentCount > 0}>
                          <span class="shrink-0">
                            {discussion.commentCount === 1
                              ? rt.t("github_issue_meta.comments_one")
                              : rt.t("github_issue_meta.comments", { count: String(discussion.commentCount) })}
                          </span>
                        </Show>
                      </div>
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().hasMore}>
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  {rt.t("github_discussions_card.has_more")}
                </div>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
